<script>
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import BaseDropdownMenuItemType from '@/shared/constants/baseComponents/BaseDropdownMenuItemType'
import interactionMixin from '@/mixins/interactionMixin'
import BaseButton from './_base-button.vue'
import CandidateImage from './candidate/candidate-image.vue'

export default {
  name: 'BaseDropdownMenu',
  components: { BaseButton, CandidateImage },
  mixins: [interactionMixin],
  props: {
    /**
     * @type {MenuItem}
     */
    menuItems: {
      type: Array,
      default: () => [],
    },
    icon: {
      type: String,
      default: 'mdi-dots-vertical',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    closeOnClick: {
      type: Boolean,
      default: false,
    },
    hideHeaderDivider: {
      type: Boolean,
      default: false,
    },
    candidateList: {
      type: Boolean,
      default: false,
    },
    iconSize: {
      type: String,
      default: '40',
    },
    iconColor: {
      type: String,
      default: 'black',
    },
    compactIcons: {
      type: Boolean,
      default: false,
    },
    subtitles: {
      type: Boolean,
      default: false,
    },
    subtitleColor: {
      type: String,
      default: 'text--black',
    },
    selectedId: {
      type: String,
      default: '',
    },
    disableListItem: {
      type: Function,
      default: () => false,
    },
    width: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    blue200: VuetifyColourClass.blue200,
    blue400: VuetifyColourClass.blue400,
    isMenuActive: false,
  }),
  computed: {
    hasMenuItems() {
      return this.menuItems.length > 0
    },
    isMultiOption() {
      return this.menuItems.length > 1
    },
  },
  watch: {
    isMenuActive(newVal) {
      this.$emit('menu-state-changed', newVal)
    },
  },
  methods: {
    isDivider(item) {
      return item.type === BaseDropdownMenuItemType.divider
    },
    isActionable(item) {
      return item.type === BaseDropdownMenuItemType.action
    },
    isHeader(item, index) {
      return item.type === BaseDropdownMenuItemType.header || index < 1
    },
    showHeaderDivider(item, index) {
      return (
        this.isHeader(item, index) &&
        this.isMultiOption &&
        !this.hideHeaderDivider
      )
    },
    useCandidateImage(item) {
      const requiredKeys = ['fullName', 'candidateId']

      return (
        this.candidateList &&
        requiredKeys.every(
          (key) =>
            Object.prototype.hasOwnProperty.call(item, key) &&
            item[key] !== null
        )
      )
    },
    closeMenu() {
      this.$refs.dropdownMenu.isActive = false
    },
    isSelected({ id }) {
      return this.selectedId === id
    },
  },
}
</script>

<template>
  <v-menu
    ref="dropdownMenu"
    v-model="isMenuActive"
    :disabled="disabled || !hasMenuItems"
    offset-y
    :close-on-content-click="closeOnClick"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" v-bind="{ ...attrs, on, isActive: isMenuActive }">
        <BaseButton
          :disabled="disabled"
          fab
          :icon="icon"
          active
          v-bind="{ ...$attrs, ...attrs }"
          v-on="on"
        />
      </slot>
    </template>

    <v-list nav :two-line="subtitles" :max-width="width">
      <template v-for="(menuItem, index) in menuItems">
        <v-divider
          v-if="isDivider(menuItem)"
          :key="`base-dropdown-menu__${menuItem.id}-divider`"
        />
        <v-list-item
          v-if="isActionable(menuItem)"
          :key="`base-dropdown-menu__${menuItem.id}`"
          v-bind="menuItem.attributes"
          :input-value="isSelected(menuItem)"
          active-class="active-list-item"
          :disabled="disableListItem(menuItem)"
          @click.prevent.stop="handleInteraction(menuItem.interaction)"
        >
          <CandidateImage
            v-if="useCandidateImage(menuItem)"
            :id="menuItem.candidateId"
            size="sm"
            :class="{ 'opacity-50': disableListItem(menuItem) }"
            :full-name="menuItem.fullName"
          />
          <v-icon
            v-else-if="compactIcons"
            :size="iconSize"
            :color="iconColor"
            class="mr-4 align-unfilled-icon"
            >{{ menuItem.icon }}</v-icon
          >
          <v-list-item-icon v-else>
            <v-icon>{{ menuItem.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              {{ menuItem.text }}
            </v-list-item-title>
            <v-list-item-subtitle
              v-if="subtitles"
              :class="menuItem.getSubtitleCSSClasses() || subtitleColor"
              >{{ menuItem.getSubtitleText() }}</v-list-item-subtitle
            >
          </v-list-item-content>
          <v-icon v-if="isSelected(menuItem)" :color="blue400"
            >mdi-check</v-icon
          >
        </v-list-item>
        <v-divider
          v-if="showHeaderDivider(menuItem, index)"
          :key="`base-dropdown-menu__${menuItem.id}-divider`"
          class="mb-2"
        />
      </template>
    </v-list>
  </v-menu>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';

.v-menu__content {
  top: 141px;
  left: 1461px;
  width: 216px;
  min-width: 216px;
  border-width: 1px;
  border-radius: 8px;
  box-shadow: 0 4px 4px -1px $color-black-transparent-45,
    0 4px 4px -1px $color-black-transparent-50;
}

.v-list-item {
  &:hover {
    background-color: $color-info-250;
  }
  &::before {
    background-color: unset;
  }
}

.v-list-item__content {
  padding: 0;
}

.v-list {
  border: 1px solid $color-neutral-grey-200;
  border-radius: 8px;
}

.v-list-item__icon:first-child {
  margin-right: 5px;

  .v-icon {
    font-size: 20px;
    color: $color-neutral-grey-400;
  }
}

.v-list-item__title {
  height: 44px;
  padding: 14px 0 !important;
  font-size: 14px;
  font-weight: 500;
  color: $color-gray-600;
  border-radius: 8px;
  gap: 12px;
}

.active-list-item {
  background: $color-blue-200;

  &::before {
    background-color: transparent;
  }
}
</style>
